// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, inject, OnInit } from '@angular/core';
import { IStepOption, TourService } from 'ngx-ui-tour-md-menu';
import { IManageUser } from '../../../../types/user';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/browser';
import { Observable } from 'rxjs';
import { selectUser } from '../../../store/user/user.selectors';
import { RestConfigService } from '../../../../services/config-service';
import { AppState } from '../../../store/app.state';
import { Store } from '@ngrx/store';
import { UserService } from '../../../../services/user-service';
import { TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs/operators';
import authHelper from '../../../auth/authHelper';

@Component({
    selector: 'app-user-tour',
    templateUrl: './user-tour.component.html',
    styleUrl: './user-tour.component.scss',
    standalone: false
})
export class UserTourComponent implements OnInit {
    protected readonly tourService = inject(TourService);
    private readonly configService = inject(RestConfigService);
    private readonly store = inject(Store<AppState>);
    private readonly userService: UserService = inject(UserService);
    private readonly translate = inject(TranslateService);
    private readonly user$: Observable<IManageUser> = this.store.select(selectUser());

    // stepId format "YYYY-MM-DD HH:mm:ss" and must be unique
    // The date for the stepId should NEVER be in the future
    // ngx-ui-tour docs https://hakimio.github.io/ngx-ui-tour/md-menu
    private tourSteps: IStepOption[] = [
        {
            stepId: '2025-01-01 00:00:01',
            anchorId: 'nav-menu-dashboard',
            title: 'Dashboard Page',
            // title: `${this.translate.instant('tour.nav-menu-dashboard.title')}`,
            content: 'Dashboard provides an actionable summary of important activity as ' +
                'well as protections in use across the entire estate.',
            // content: `${this.translate.instant('tour.nav-menu-dashboard.content')}`,
            placement: {horizontal: true},
            isAsync: true,
            isOptional: true,
            asyncStepTimeout: 2000,
        },
        {
            stepId: '2025-01-01 00:00:02',
            anchorId: 'search-all-button',
            title: 'General Search',
            content: 'Start typing the email address that you logged in with.',
            isOptional: true,
        },
        {
            stepId: '2025-01-01 00:00:03',
            anchorId: 'search-all-results',
            title: 'Search Results',
            content: 'Your email is shown in the results under the category Users. Search ' +
                'throughout application for users, endpoints, groups, policies, etc. Selecting ' +
                'a result will take you to the page for that result.',
            isOptional: true,
        },
        {
            stepId: '2025-01-01 00:00:04',
            anchorId: 'search-all-clear',
            title: 'Clear Search Results',
            content: 'Click to clear the results and hide the results window.',
            isOptional: true,
        },
        {
            stepId: '2025-01-01 00:00:05',
            anchorId: 'help-menu-button',
            title: 'Help Menu',
            content: 'Context-sensitive help menu depending on your current location.',
        },
        {
            stepId: '2025-01-01 00:00:06',
            anchorId: 'nav-menu-endpoints',
            title: 'Endpoints Page',
            content: 'The Endpoints page provides reporting and management of Data Defense deployments within your ' +
                'subscription including PIN Reset and changing Manual Lock states.',
            route: 'endpoints',
            isAsync: true,
            isOptional: true,
            asyncStepTimeout: 2000,
            placement: {horizontal: true},
            enableBackdrop: true,
        },
        {
            stepId: '2025-01-01 00:00:07',
            anchorId: 'endpoints-column-chooser',
            title: 'Column Chooser',
            content: 'The Endpoints page provides reporting and management of Data Defense deployments within your ' +
                'subscription including PIN Reset and changing Manual Lock states.',
            placement: {xPosition: 'before'},
            isAsync: true,
            isOptional: true,
            asyncStepTimeout: 2000,
        },
    ];

    ngOnInit() {
        this.tourService.end$.pipe(
            switchMap(() => this.userService.dismissTour())
        ).subscribe();
        this.user$.subscribe(user => this.initializeTour(user, false));
    }

    private getUsersTourSteps(user: IManageUser): IStepOption[] {
        let invalidStepIds: string[] = [];
        let userDismissedDate = dayjs.utc(user?.tour_dismissed_dttm);
        userDismissedDate = userDismissedDate.isValid() ? userDismissedDate : dayjs.utc('0000-00-00 00:00:00');
        const utcNow = dayjs.utc();

        const steps = this.tourSteps.filter(s => {
            const stepDate = dayjs.utc(s.stepId);
            const validStepDate = stepDate.isValid() && stepDate.isBefore(utcNow);
            if (!validStepDate) {
                invalidStepIds.push(s.stepId);
            }
            return validStepDate && stepDate.isAfter(userDismissedDate);
        });
        if (invalidStepIds.length > 0) {
            Sentry.captureMessage(`Invalid tour stepId(s), ${invalidStepIds.toString()}`);
        }
        return steps;
    }

    public initializeTour(user: IManageUser, useAllSteps: boolean) {
        if (authHelper.isUserTourEnabled(user)) {
            this.configService.getRestConfig()
            .subscribe(config => {
                if (config.enable_tour) {
                    const usersTourSteps = useAllSteps ? this.tourSteps : this.getUsersTourSteps(user);
                    if (usersTourSteps.length > 0) {
                        this.tourService.initialize(usersTourSteps, {
                            enableBackdrop: true,
                            smoothScroll: true,
                            showProgress: true,
                            nextBtnTitle: `${this.translate.instant('tour.nextBtnTitle')}`,
                            endBtnTitle: `${this.translate.instant('tour.endBtnTitle')}`,
                            backdropConfig: {
                                offset: 6,
                            }
                        });
                        setTimeout(() => {
                            this.tourService.start();
                        }, 0);
                    }
                }
            });
        }
    }

}
